<script setup>
import { ref, computed, inject, watch, onUnmounted} from 'vue'
import { useWorkspaceStore } from '@stores/WorkspaceData';
const wpStore = useWorkspaceStore()
import { planService } from '@requests/Main/plan.service';
import { unixToDate, dateToUnix } from '@services/process/Main/unix.servise';
import DatePicker from 'primevue/datepicker';
import Fieldset from 'primevue/fieldset';
import Message from 'primevue/message';
import { debounce } from 'lodash-es'
const toast = inject('toast')

const Plan = computed(() => wpStore.experiment.Plan)
const Turnaround = computed(() => Plan.value?.turnaround || null)
// Разворот считается от начала съёмки — пока время съёмки не рассчитано, форма неактивна
const ready = computed(() => Plan.value?.shootingTime?.shootingStart != null)

// Ввод пользователя: начальная ориентация, скорость вращения и выбранное время начала разворота (t transfer)
const input = ref({})
watch(Turnaround, (t) => {
  input.value = {
    initialPitch: t?.initialPitch ?? 0,
    initialRoll: t?.initialRoll ?? 0,
    initialYaw: t?.initialYaw ?? 0,
    initialPitchRate: t?.initialPitchRate ?? 0,
    initialRollRate: t?.initialRollRate ?? 0,
    initialYawRate: t?.initialYawRate ?? 0,
    turnaroundStart: unixToDate(t?.turnaroundStart),
  }
}, {immediate: true})

const SaveInput = async () => {
  await planService.PutTurnaround(Plan.value, {...input.value, turnaroundStart: dateToUnix(input.value.turnaroundStart)})
  await planService.Reload()
}
// Сохраняем только по вводу пользователя (не при подстановке данных с сервера)
const debouncedSave = debounce(SaveInput, 500)
onUnmounted(() => debouncedSave.flush())

const modelling = ref(false)
const Modelling = async () => {
  toast.info("Начинаем расчёт угла и времени разворота")
  modelling.value = true
  // Расчёт берёт начальную ориентацию из плана — сначала досохраняем ввод
  debouncedSave.cancel()
  await SaveInput()
  const result = await planService.CalculateTurnaround(Plan.value)
  await planService.Reload()
  modelling.value = false
  if(!result){
    toast.error("Не удалось провести расчёт")
    return
  }
  toast.success(`Расчёт угла и времени разворота успешен`)
}
</script>

<template>
  <Panel header="Время разворота" id="TurnaroundTime" v-if="Plan">
    <Message v-if="!ready" severity="secondary" size="small" variant="simple">Сначала выполните расчёт времени съёмки</Message>
    <div style="display: flex;flex-direction: row; gap: 0px" >
    <Fieldset legend="Начальная ориентация">
      <div class="fieldsetDiv">
        <FloatLabel variant="on">
          <InputNumber  v-model="input.initialPitch" @update:modelValue="debouncedSave" :disabled="!ready" size="small" style="width: 100%;" :maxFractionDigits="5"/>
          <label>Тангаж</label>
        </FloatLabel>
      </div>
      <div class="fieldsetDiv">
        <FloatLabel variant="on">
          <InputNumber  v-model="input.initialRoll" @update:modelValue="debouncedSave" :disabled="!ready" size="small" style="width: 100%;" :maxFractionDigits="5"/>
          <label>Крен</label>
        </FloatLabel>
      </div>
      <div class="fieldsetDiv">
        <FloatLabel variant="on">
          <InputNumber  v-model="input.initialYaw" @update:modelValue="debouncedSave" :disabled="!ready" size="small" style="width: 100%;" :maxFractionDigits="5"/>
          <label>Рысканье</label>
        </FloatLabel>
      </div>
  </Fieldset>
  <Fieldset legend="Начальная скорость вращения">
    <div class="fieldsetDiv">
        <FloatLabel variant="on">
          <InputNumber  v-model="input.initialPitchRate" @update:modelValue="debouncedSave" :disabled="!ready" size="small" style="width: 100%;" :maxFractionDigits="5"/>
          <label>Тангаж</label>
        </FloatLabel>
      </div>
      <div class="fieldsetDiv">
        <FloatLabel variant="on">
          <InputNumber  v-model="input.initialRollRate" @update:modelValue="debouncedSave" :disabled="!ready" size="small" style="width: 100%;" :maxFractionDigits="5"/>
          <label>Крен</label>
        </FloatLabel>
      </div>
      <div class="fieldsetDiv">
        <FloatLabel variant="on">
          <InputNumber  v-model="input.initialYawRate" @update:modelValue="debouncedSave" :disabled="!ready" size="small" style="width: 100%;" :maxFractionDigits="5"/>
          <label>Рысканье</label>
        </FloatLabel>
      </div>
  </Fieldset>
  </div>

  <div style="display: flex;flex: 1;flex-direction: column;">
    <Fieldset style="flex: 1;width: auto;">
      <div>
        <Button label="Расчёт угла и времени разворота" raised @click="Modelling" :icon="`pi ${modelling?'pi-spin pi-spinner':'pi-refresh'}`" iconPos="right" :disabled="modelling || !ready"/>
      </div>
      <div style="display: flex; gap: 10px; align-items: center;">
        <div class="fieldsetDiv">
          <FloatLabel variant="on">
            <InputNumber :modelValue="Turnaround?.turnAngle" size="small" style="width: 100%;" disabled :maxFractionDigits="5" suffix=" °"/>
            <label>Угол разворота</label>
          </FloatLabel>
        </div>
        <div class="fieldsetDiv">
          <FloatLabel variant="on">
            <InputNumber :modelValue="Turnaround?.turnTime" size="small" style="width: 100%;" disabled :maxFractionDigits="2" suffix=" с"/>
            <label>Время разворота</label>
          </FloatLabel>
        </div>
      </div>

    </Fieldset>
    <Fieldset style="flex: 1;width: auto;" legend="Время начала разворота">
        <div class="fieldsetDiv">
          <FloatLabel variant="on">
            <DatePicker :modelValue="unixToDate(Turnaround?.turnaroundStartComputed)" showTime hourFormat="24" showSeconds dateFormat="dd.mm.yy" disabled style="width: 100%;"/>
            <label>Расчётное</label>
          </FloatLabel>
        </div>
        <div class="fieldsetDiv">
          <FloatLabel variant="on">
            <DatePicker v-model="input.turnaroundStart" @update:modelValue="debouncedSave" :disabled="!ready" showTime hourFormat="24" showIcon showSeconds dateFormat="dd.mm.yy" style="width: 100%;"/>
            <label>Выбранное</label>
          </FloatLabel>
        </div>
    </Fieldset>
  </div>
</Panel>
</template>

<style>

#TurnaroundTime{
  margin-top: 10px;
}
#TurnaroundTime .p-panel-header{
  padding: 10px 10px 0px 10px;

}
</style>
